import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from "@/hooks/use-toast";
import { formatArmenianDate } from '@/utils/dateFormatting';
import { Calendar, Clock, MapPin, BookOpen, Save, Trash2, Users, Check, X } from 'lucide-react';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
}

interface LessonData {
  id?: string;
  lesson_number: number;
  lesson_date?: string;
  topic?: string;
  location?: string;
  time?: string;
  lecturer_id?: string;
  phase_id: string;
  group_id: string;
  has_attendance: boolean;
}

interface AttendanceRecord {
  lesson_id: string;
  student_id: string;
  is_present: boolean;
}

interface LessonAttendanceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  lesson: LessonData | null;
  studentId?: string;
  studentName?: string;
  currentAttendanceStatus?: boolean | null;
  onSave: () => Promise<void>;
  onDelete: (lessonId: string) => Promise<void>;
  students: Student[];
  onAttendanceUpdate?: (lessonId: string, studentId: string, isPresent: boolean) => void;
}

// Armenian text constants using Unicode
const TEXT = {
  LESSON: "\u0534\u0561\u057D",
  CREATED: "\u054D\u057F\u0565\u0572\u056E\u057E\u0561\u056E",
  NEW_LESSON: "\u0546\u0578\u0580 \u0564\u0561\u057D",
  DATE: "\u0531\u0574\u057D\u0561\u0569\u056B\u057E",
  TIME: "\u0546\u0561\u0574",
  LOCATION: "\u054E\u0561\u0575\u0580",
  LOCATION_PLACEHOLDER: "\u0534\u0561\u057D\u056B \u057E\u0561\u0575\u0580\u0568",
  ATTENDANCE: "\u0540\u0561\u0573\u0561\u056D\u0565\u056C\u056B\u0578\u0582\u0569\u0575\u0578\u0582\u0576",
  PRESENT: "\u0576\u0565\u0580\u056F\u0561",
  TOPIC: "\u0539\u0565\u0574\u0561",
  TOPIC_PLACEHOLDER: "\u0534\u0561\u057D\u056B \u0569\u0565\u0574\u0561\u0576",
  ALL_PRESENT: "\u0532\u0578\u056C\u0578\u0580\u0568 \u0576\u0565\u0580\u056F\u0561",
  ALL_ABSENT: "\u0532\u0578\u056C\u0578\u0580\u0568 \u0562\u0561\u0581\u0561\u056F\u0561",
  STUDENT: "\u0548\u0582\u057D\u0561\u0576\u0578\u0572",
  STATUS: "\u053F\u0561\u0580\u0563\u0561\u057E\u056B\u0573\u0561\u056F",
  ACTION: "\u0533\u0578\u0580\u056E\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576",
  SELECTED: "\u0538\u0576\u057F\u0580\u057E\u0561\u056E",
  PRESENT_STATUS: "\u0546\u0565\u0580\u056F\u0561",
  ABSENT_STATUS: "\u0532\u0561\u0581\u0561\u056F\u0561",
  MARK_ABSENT: "\u0532\u0561\u0581\u0561\u056F\u0561 \u0564\u0561\u0580\u0571\u0576\u0565\u056C",
  MARK_PRESENT: "\u0546\u0565\u0580\u056F\u0561 \u0564\u0561\u0580\u0571\u0576\u0565\u056C",
  DELETING: "\u054B\u0576\u057B\u057E\u0578\u0582\u0574 \u0567...",
  DELETE_LESSON: "\u054B\u0576\u057B\u0565\u056C \u0564\u0561\u057D\u0568",
  CANCEL: "\u0549\u0565\u0572\u0561\u0580\u056F\u0565\u056C",
  SAVING: "\u054A\u0561\u0570\u057A\u0561\u0576\u057E\u0578\u0582\u0574 \u0567...",
  SAVE: "\u054A\u0561\u0570\u057A\u0561\u0576\u0565\u056C",
  SUCCESS: "\u0540\u0561\u057B\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576",
  ERROR: "\u054D\u056D\u0561\u056C",
  WARNING: "\u0536\u0563\u0578\u0582\u0577\u0561\u0581\u0578\u0582\u0574",
  LESSON_CREATED: "\u0534\u0561\u057D\u0568 \u057D\u057F\u0565\u0572\u056E\u057E\u0565\u0581",
  LESSON_UPDATED: "\u0534\u0561\u057D\u056B \u057F\u057E\u0575\u0561\u056C\u0576\u0565\u0580\u0568 \u0569\u0561\u0580\u0574\u0561\u0581\u057E\u0565\u0581\u056B\u0576",
  LESSON_COMPLETED: "\u0534\u0561\u057D\u0568 \u0576\u0577\u057E\u0561\u056E \u0567 \u0561\u057E\u0561\u0580\u057F\u057E\u0561\u056E",
  LESSON_DELETED: "\u0534\u0561\u057D\u0568 \u057B\u0576\u057B\u057E\u0565\u0581",
  ATTENDANCE_UPDATED: "\u0546\u0565\u0580\u056F\u0561\u0575\u0578\u0582\u0569\u0575\u0561\u0576 \u056F\u0561\u0580\u0563\u0561\u057E\u056B\u0573\u0561\u056F",
  MISSING_DATA: "\u0532\u0561\u0581\u0561\u056F\u0561\u0575\u0578\u0582\u0574 \u0565\u0576 \u0561\u0576\u0570\u0580\u0561\u056A\u0565\u0577\u057F \u057F\u057E\u0575\u0561\u056C\u0576\u0565\u0580\u0568",
  DATE_REQUIRED: "\u0531\u0574\u057D\u0561\u0569\u056B\u057E\u0568 \u057A\u0561\u0580\u057F\u0561\u0564\u056B\u0580 \u0567 \u0564\u0561\u057D\u056B \u057F\u057E\u0575\u0561\u056C\u0576\u0565\u0580\u0568 \u057A\u0561\u0570\u057A\u0561\u0576\u0565\u056C\u0578\u0582 \u0570\u0561\u0574\u0561\u0580",
  STATUS_UPDATE_WARNING: "\u0534\u0561\u057D\u056B \u056F\u0561\u0580\u0563\u0561\u057E\u056B\u0573\u0561\u056F\u0568 \u0570\u0576\u0561\u0580\u0561\u057E\u0578\u0580 \u0567 \u0579\u0569\u0561\u0580\u0574\u0561\u0581\u057E\u056B, \u0562\u0561\u0575\u0581 \u0570\u0561\u0573\u0561\u056D\u0565\u056C\u056B\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0568 \u057A\u0561\u0570\u057A\u0561\u0576\u057E\u0565\u0581",
  SAVE_ERROR: "\u0549\u0570\u0561\u057B\u0578\u0572\u057E\u0565\u0581 \u057A\u0561\u0570\u057A\u0561\u0576\u0565\u056C \u0564\u0561\u057D\u0568",
  DELETE_ERROR: "\u0549\u0570\u0561\u057B\u0578\u0572\u057E\u0565\u0581 \u057B\u0576\u057B\u0565\u056C \u0564\u0561\u057D\u0568"
};

export const LessonAttendanceDialog = ({
  isOpen,
  onClose,
  lesson,
  studentId,
  studentName,
  currentAttendanceStatus,
  onSave,
  onDelete,
  students,
  onAttendanceUpdate
}: LessonAttendanceDialogProps) => {
  const [formData, setFormData] = useState({
    lesson_date: '',
    topic: '',
    location: '',
    time: ''
  });
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (lesson) {
      setFormData({
        lesson_date: lesson.lesson_date || '',
        topic: lesson.topic || '',
        location: lesson.location || '',
        time: lesson.time || ''
      });

      if (lesson.id) {
        fetchAttendance(lesson.id);
      } else {
        setAttendance(students.map(student => ({
          lesson_id: '',
          student_id: student.id,
          is_present: false
        })));
      }
    }
  }, [lesson, students]);

  useEffect(() => {
    if (studentId && currentAttendanceStatus !== undefined && attendance.length > 0) {
      const newStatus = currentAttendanceStatus === null ? true : !currentAttendanceStatus;
      setAttendance(prev => prev.map(record =>
        record.student_id === studentId
          ? { ...record, is_present: newStatus }
          : record
      ));
      
      if (studentName) {
        toast({
          title: TEXT.ATTENDANCE_UPDATED,
          description: `${studentName} - ${newStatus ? TEXT.PRESENT_STATUS : TEXT.ABSENT_STATUS}`,
        });
      }
    }
  }, [studentId, currentAttendanceStatus, attendance.length, studentName, toast]);

  const fetchAttendance = async (lessonId: string) => {
    try {
      const { data, error } = await supabase
        .from('attendance')
        .select('*')
        .eq('lesson_id', lessonId);

      if (error) throw error;

      const attendanceMap = new Map(data?.map(record => [record.student_id, record]) || []);
      const fullAttendance = students.map(student => ({
        lesson_id: lessonId,
        student_id: student.id,
        is_present: attendanceMap.get(student.id)?.is_present || false
      }));

      setAttendance(fullAttendance);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const handleSave = async () => {
    if (!lesson || !user) {
      toast({ title: TEXT.ERROR, description: TEXT.MISSING_DATA, variant: "destructive" });
      return;
    }

    const hasLessonChanges = formData.lesson_date || formData.topic || formData.location || formData.time;
    if (hasLessonChanges && !formData.lesson_date) {
      toast({ title: TEXT.ERROR, description: TEXT.DATE_REQUIRED, variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      let lessonId = lesson.id;

      if (!lessonId) {
        const { data: newLesson, error: lessonError } = await supabase
          .from('lessons')
          .insert([{
            phase_id: lesson.phase_id,
            group_id: lesson.group_id,
            lesson_number: lesson.lesson_number,
            lesson_date: formData.lesson_date,
            topic: formData.topic || null,
            location: formData.location || null,
            time: formData.time || null,
            lecturer_id: user.id,
            status: 'scheduled'
          }])
          .select()
          .single();

        if (lessonError) throw lessonError;
        lessonId = newLesson.id;
        toast({ title: TEXT.SUCCESS, description: TEXT.LESSON_CREATED });
      } else {
        const { error: updateError } = await supabase
          .from('lessons')
          .update({
            lesson_date: formData.lesson_date,
            topic: formData.topic || null,
            location: formData.location || null,
            time: formData.time || null
          })
          .eq('id', lessonId);

        if (updateError) throw updateError;
        toast({ title: TEXT.SUCCESS, description: TEXT.LESSON_UPDATED });
      }

      for (const attendanceRecord of attendance) {
        const { error: attendanceError } = await supabase
          .from('attendance')
          .upsert({
            lesson_id: lessonId,
            student_id: attendanceRecord.student_id,
            is_present: attendanceRecord.is_present,
            marked_at: new Date().toISOString()
          }, { onConflict: 'lesson_id,student_id' });

        if (!attendanceError && onAttendanceUpdate) {
          onAttendanceUpdate(lessonId, attendanceRecord.student_id, attendanceRecord.is_present);
        }
      }

      const { error: statusUpdateError } = await supabase
        .from('lessons')
        .update({ status: 'completed', marked_by_lecturer_id: user.id })
        .eq('id', lessonId);

      if (statusUpdateError) {
        toast({ title: TEXT.WARNING, description: TEXT.STATUS_UPDATE_WARNING, variant: "default" });
      } else {
        toast({ title: TEXT.SUCCESS, description: TEXT.LESSON_COMPLETED });
      }

      await onSave();
      onClose();
    } catch (error) {
      toast({ title: TEXT.ERROR, description: `${TEXT.SAVE_ERROR}: ${error.message}`, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!lesson?.id) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('lessons').delete().eq('id', lesson.id);
      if (error) throw error;
      toast({ title: TEXT.SUCCESS, description: TEXT.LESSON_DELETED });
      await onDelete(lesson.id);
      onClose();
    } catch (error) {
      toast({ title: TEXT.ERROR, description: TEXT.DELETE_ERROR, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const toggleAttendance = (studentId: string) => {
    setAttendance(prev => prev.map(record =>
      record.student_id === studentId ? { ...record, is_present: !record.is_present } : record
    ));
  };

  const getAttendanceCount = () => {
    const present = attendance.filter(record => record.is_present).length;
    return { present, total: attendance.length };
  };

  if (!lesson) return null;
  const attendanceCount = getAttendanceCount();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-muted border-border/70">
        <DialogHeader className="border-b border-border/70 pb-4">
          <DialogTitle className="text-muted-foreground font-mono flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {TEXT.LESSON} {lesson.lesson_number}
            {lesson.id ? (
              <Badge variant="secondary" className="bg-primary/10 text-muted-foreground">{TEXT.CREATED}</Badge>
            ) : (
              <Badge variant="outline" className="bg-muted/50 text-muted-foreground">{TEXT.NEW_LESSON}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="lesson_date" className="text-muted-foreground font-mono flex items-center gap-2">
                <Calendar className="h-4 w-4" />{TEXT.DATE}
              </Label>
              <Input id="lesson_date" type="date" value={formData.lesson_date}
                onChange={(e) => setFormData(prev => ({ ...prev, lesson_date: e.target.value }))}
                className="bg-muted border-border/70 text-foreground font-mono" required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="time" className="text-muted-foreground font-mono flex items-center gap-2">
                <Clock className="h-4 w-4" />{TEXT.TIME}
              </Label>
              <Input id="time" type="time" value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="bg-muted border-border/70 text-foreground font-mono" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location" className="text-muted-foreground font-mono flex items-center gap-2">
                <MapPin className="h-4 w-4" />{TEXT.LOCATION}
              </Label>
              <Input id="location" value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="bg-muted border-border/70 text-foreground font-mono" placeholder={TEXT.LOCATION_PLACEHOLDER} />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground font-mono flex items-center gap-2">
                <Users className="h-4 w-4" />{TEXT.ATTENDANCE}
              </Label>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-muted-foreground border-border/70">
                  {attendanceCount.present} / {attendanceCount.total}
                </Badge>
                <span className="text-sm text-muted-foreground">{TEXT.PRESENT}</span>
              </div>
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="topic" className="text-muted-foreground font-mono">{TEXT.TOPIC}</Label>
              <Textarea id="topic" value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                className="bg-muted border-border/70 text-foreground font-mono" placeholder={TEXT.TOPIC_PLACEHOLDER} rows={3} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-muted-foreground font-mono font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />{TEXT.ATTENDANCE}
              </h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  onClick={() => setAttendance(prev => prev.map(record => ({ ...record, is_present: true })))}
                  className="text-primary border-border/70 hover:bg-green-400/10">
                  <Check className="h-4 w-4 mr-1" />{TEXT.ALL_PRESENT}
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => setAttendance(prev => prev.map(record => ({ ...record, is_present: false })))}
                  className="text-destructive border-destructive/40 hover:bg-red-400/10">
                  <X className="h-4 w-4 mr-1" />{TEXT.ALL_ABSENT}
                </Button>
              </div>
            </div>

            <div className="border border-border/70 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/70">
                    <TableHead className="text-muted-foreground font-mono">{TEXT.STUDENT}</TableHead>
                    <TableHead className="text-muted-foreground font-mono text-center">{TEXT.STATUS}</TableHead>
                    <TableHead className="text-muted-foreground font-mono text-center">{TEXT.ACTION}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student) => {
                    const attendanceRecord = attendance.find(record => record.student_id === student.id);
                    const isPresent = attendanceRecord?.is_present || false;
                    return (
                      <TableRow key={student.id} className="border-border/70">
                        <TableCell className="text-foreground font-mono">
                          {student.first_name} {student.last_name}
                          {studentId === student.id && (
                            <Badge variant="outline" className="ml-2 text-muted-foreground border-border/70">{TEXT.SELECTED}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={isPresent ? "default" : "destructive"}
                            className={isPresent ? "bg-primary hover:bg-primary/90" : "bg-red-600 hover:bg-red-700"}>
                            {isPresent ? TEXT.PRESENT_STATUS : TEXT.ABSENT_STATUS}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button size="sm" variant="outline" onClick={() => toggleAttendance(student.id)}
                            className={isPresent ? "text-destructive border-destructive/40 hover:bg-red-400/10" : "text-primary border-border/70 hover:bg-green-400/10"}>
                            {isPresent ? (<><X className="h-4 w-4 mr-1" />{TEXT.MARK_ABSENT}</>) : (<><Check className="h-4 w-4 mr-1" />{TEXT.MARK_PRESENT}</>)}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-border/70">
            <div>
              {lesson.id && (
                <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
                  {deleting ? TEXT.DELETING : (<><Trash2 className="h-4 w-4 mr-2" />{TEXT.DELETE_LESSON}</>)}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="text-muted-foreground border-border">{TEXT.CANCEL}</Button>
              <Button onClick={handleSave} disabled={saving || !formData.lesson_date} className="bg-primary hover:bg-primary/90">
                {saving ? TEXT.SAVING : (<><Save className="h-4 w-4 mr-2" />{TEXT.SAVE}</>)}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
